/* Vitality Capsules custom PDP: gallery slider + buy box. No dependencies. */
(() => {
  if (window.__vcPdpLoaded) return;
  window.__vcPdpLoaded = true;

  // Builds the cart lines for one offer from its data-variant/qty/plan attributes.
  // One-time bundles carry the Kaching deal property so Kaching's discount applies.
  // An option can also carry a gift product (data-gift-variant), added as its own line.
  function buildItems({ variant, qty, plan, kaching, giftVariant, propName, propValue }) {
    const item = { id: Number(variant), quantity: Number(qty) || 1 };
    if (plan) item.selling_plan = Number(plan);
    const properties = {};
    if (kaching) properties.__kaching_bundles = kaching;
    if (propName && propValue) properties[propName] = propValue;
    if (Object.keys(properties).length) item.properties = properties;
    const items = [item];
    if (giftVariant) items.push({ id: Number(giftVariant), quantity: 1 });
    return items;
  }

  // ---- Kaching Bundles ----
  // The Kaching app embed prints each product's deal settings as JSON. Bundle
  // discounts only apply when the cart line has the __kaching_bundles property
  // (deal id, product, version, deal bar), exactly as Kaching's own widget adds it.
  function kachingDeal(productId) {
    const el = document.querySelector(`script.kaching-bundles-deal-block-settings[data-product-id="${productId}"]`);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function kachingBar(deal, qty) {
    if (!deal || !Array.isArray(deal.dealBars)) return null;
    return deal.dealBars.find((bar) => bar.dealBarType === 'quantity-break' && Number(bar.quantity) === Number(qty)) || null;
  }

  function kachingProperty(deal, bar, productId) {
    return JSON.stringify({ deal: deal.nanoId, pp: Number(productId), main: true, v: deal.versionId, bid: bar.id });
  }

  // Price Kaching will charge for a bar, in cents ('specific' = fixed bundle total).
  function kachingPrice(bar, compareCents) {
    const value = Number(bar.discountValue);
    switch (bar.discountType) {
      case 'specific':
        return Math.round(value * 100);
      case 'percentage':
        return Math.round(compareCents * (1 - value / 100));
      case 'fixed':
        return compareCents - Math.round(value * 100);
      default:
        return compareCents;
    }
  }

  const money = (cents) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD' }).format(cents / 100);

  // Wires one-time offers to the live Kaching deal. Returns false for offers that
  // must be hidden (a multi-bottle bundle with no matching Kaching deal would
  // otherwise be charged at full price).
  function applyKaching(input, deal, productId) {
    const qty = Number(input.dataset.kachingQty);
    const bar = kachingBar(deal, qty);
    if (!bar) return qty <= 1;
    input.dataset.kaching = kachingProperty(deal, bar, productId);
    const compare = Number(input.dataset.compareCents);
    const price = kachingPrice(bar, compare);
    if (price !== Number(input.dataset.priceCents)) {
      // Kaching deal changed since the theme was set up: show what checkout will charge.
      const card = input.closest('.vc-offer');
      input.dataset.priceCents = price;
      input.dataset.price = money(price);
      input.dataset.save = price < compare ? money(compare - price) : '';
      card.querySelector('[data-price-display]').textContent = money(price);
      card.querySelector('[data-compare-display]').hidden = price >= compare;
      const pill = card.querySelector('[data-save-pill]');
      pill.hidden = price >= compare;
      pill.querySelector('[data-save-amount]').textContent = money(compare - price);
    }
    return true;
  }

  async function addToCart(items, { cartAddUrl, cartUrl }, button, errorEl) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    errorEl.hidden = true;
    try {
      const res = await fetch(`${cartAddUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');
      document.dispatchEvent(new CustomEvent('vc:cart-added', { detail: data }));
      openCart(cartUrl);
    } catch (err) {
      errorEl.textContent = err.message;
      errorEl.hidden = false;
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  // Kaching Cart (app embed) opens its drawer when a cart link is clicked.
  // Without it the theme uses the cart page, so go there.
  function openCart(cartUrl) {
    const cartLink = document.querySelector('kaching-cart') && document.querySelector('#cart-icon-bubble, a[href$="/cart"]');
    if (cartLink) {
      cartLink.click();
    } else {
      window.location.href = cartUrl;
    }
  }

  class VcGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.track.children);
      const dots = this.querySelector('[data-dots]');
      const prev = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');

      if (this.slides.length < 2) {
        prev.hidden = true;
        next.hidden = true;
        return;
      }

      this.dots = this.slides.map((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'vc-gallery__dot';
        dot.setAttribute('aria-label', `Go to image ${i + 1}`);
        dot.addEventListener('click', () => this.goTo(i));
        dots.appendChild(dot);
        return dot;
      });

      prev.addEventListener('click', () => this.goTo(this.index - 1));
      next.addEventListener('click', () => this.goTo(this.index + 1));
      // Thumbnails reuse each slide's srcset so the browser picks a small file.
      const thumbs = this.parentElement.querySelector('.vc-gallery__thumbs');
      this.thumbs = thumbs
        ? this.slides.map((slide, i) => {
            const img = slide.querySelector('img');
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'vc-gallery__thumb';
            btn.setAttribute('aria-label', `Show image ${i + 1}`);
            if (img) {
              const t = document.createElement('img');
              t.src = img.src;
              if (img.srcset) t.srcset = img.srcset;
              t.sizes = '80px';
              t.alt = '';
              t.loading = 'lazy';
              btn.appendChild(t);
            }
            btn.addEventListener('click', () => this.goTo(i));
            thumbs.appendChild(btn);
            return btn;
          })
        : [];

      this.track.addEventListener('scroll', () => this.sync(), { passive: true });
      this.sync();
    }

    get index() {
      return Math.round(this.track.scrollLeft / this.track.clientWidth);
    }

    goTo(i) {
      const count = this.slides.length;
      const target = (i + count) % count;
      this.track.scrollTo({ left: target * this.track.clientWidth, behavior: 'smooth' });
    }

    sync() {
      const current = this.index;
      [this.dots, this.thumbs].forEach((list) =>
        list.forEach((el, i) => el.setAttribute('aria-current', i === current ? 'true' : 'false'))
      );
    }
  }

  class VcBuyBox extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-atc]');
      this.label = this.querySelector('[data-atc-label]');
      this.saveEl = this.querySelector('[data-atc-save]');
      this.errorEl = this.querySelector('[data-atc-error]');
      const tpl = this.querySelector('[data-atc-template]');
      this.labelTemplate = tpl ? JSON.parse(tpl.textContent) : 'Add to Cart | [price]';

      const deal = kachingDeal(this.dataset.productId);
      this.querySelectorAll('input[data-kaching-qty]').forEach((input) => {
        if (!applyKaching(input, deal, this.dataset.productId)) input.closest('.vc-offer').remove();
      });

      this.tabs = Array.from(this.querySelectorAll('[data-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-panel]'));
      this.tabs.forEach((tab) => tab.addEventListener('click', () => this.selectTab(tab.dataset.tab)));
      this.addEventListener('change', (e) => {
        if (e.target.matches('input[type="radio"]')) this.update();
      });
      this.button.addEventListener('click', () => this.addToCart());
      this.initSticky();

      this.selectTab(this.panels[0] ? this.panels[0].dataset.panel : 'subscribe');
    }

    get activePanel() {
      return this.panels.find((p) => !p.hidden);
    }

    get selected() {
      const panel = this.activePanel;
      return panel ? panel.querySelector('input[type="radio"]:checked') : null;
    }

    selectTab(name) {
      this.mode = name;
      this.tabs.forEach((tab) => tab.setAttribute('aria-selected', tab.dataset.tab === name ? 'true' : 'false'));
      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.panel !== name;
      });
      const panel = this.activePanel;
      if (panel && !panel.querySelector('input:checked')) {
        const first = panel.querySelector('input[data-default]') || panel.querySelector('input[type="radio"]');
        if (first) first.checked = true;
      }
      this.querySelectorAll('[data-sub-only]').forEach((el) => {
        el.hidden = name !== 'subscribe';
      });
      this.update();
    }

    // Sticky bar: shown once the main button has scrolled above the viewport.
    initSticky() {
      this.sticky = this.querySelector('[data-sticky]');
      if (!this.sticky) return;
      this.stickyButton = this.sticky.querySelector('[data-sticky-atc]');
      this.stickyLabel = this.sticky.querySelector('[data-sticky-label]');
      this.stickyOption = this.sticky.querySelector('[data-sticky-option]');
      this.stickyButton.addEventListener('click', () => this.addToCart(this.stickyButton));
      const setVisible = (visible) => {
        this.sticky.classList.toggle('is-visible', visible);
        this.sticky.inert = !visible;
      };
      // Checked on scroll (once per frame) rather than with IntersectionObserver,
      // which misses jumps past the button (anchor links, fast flings).
      let queued = false;
      const check = () => {
        queued = false;
        setVisible(this.button.getBoundingClientRect().bottom < 0);
      };
      const onScroll = () => {
        if (!queued) {
          queued = true;
          requestAnimationFrame(check);
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      check();
    }

    update() {
      const input = this.selected;
      if (!input || !this.label) return;
      this.label.textContent = this.labelTemplate.replace('[price]', input.dataset.price);
      if (this.sticky) {
        const mode = this.mode === 'subscribe' ? 'Subscribe & Save' : 'One-time';
        this.stickyOption.textContent = `${input.dataset.title} · ${mode}`;
        this.stickyLabel.textContent = this.labelTemplate.replace('[price]', input.dataset.price);
      }
      const save = input.dataset.save;
      this.saveEl.hidden = !save;
      this.saveEl.textContent = save ? `You save ${save} today` : '';
      this.errorEl.hidden = true;
    }

    addToCart(button = this.button) {
      const input = this.selected;
      if (!input) return;
      addToCart(buildItems(input.dataset), this.dataset, button, this.errorEl);
    }
  }

  class VcQuickAdd extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('button');
      const errorEl = this.querySelector('[role="alert"]');
      if (this.dataset.kachingQty) {
        const deal = kachingDeal(this.dataset.productId);
        const bar = kachingBar(deal, this.dataset.kachingQty);
        if (bar) {
          this.dataset.kaching = kachingProperty(deal, bar, this.dataset.productId);
        } else if (Number(this.dataset.kachingQty) > 1) {
          // No live deal for this bundle: hide the whole offer section rather than charge full price.
          const section = this.closest('.vc');
          if (section) section.hidden = true;
          return;
        }
      }
      button.addEventListener('click', () =>
        addToCart(buildItems(this.dataset), this.dataset, button, errorEl)
      );
    }
  }

  // Customer videos: play muted while on screen; tapping a video turns its sound on
  // (and mutes the others).
  class VcUgc extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.vc-ugc__item')).filter((item) => item.querySelector('video'));
      const observer = new IntersectionObserver(
        (entries) =>
          entries.forEach(({ target, isIntersecting }) => {
            const video = target.querySelector('video');
            if (isIntersecting) video.play().catch(() => {});
            else video.pause();
          }),
        { threshold: 0.4 }
      );
      this.items.forEach((item) => {
        observer.observe(item);
        item.addEventListener('click', () => this.toggleSound(item));
      });
    }

    toggleSound(item) {
      const video = item.querySelector('video');
      const turnOn = video.muted;
      this.items.forEach((other) => {
        other.querySelector('video').muted = true;
        other.querySelector('.vc-ugc__sound').setAttribute('aria-pressed', 'false');
      });
      video.muted = !turnOn;
      if (turnOn) {
        video.currentTime = 0;
        video.play().catch(() => {});
      }
      const button = item.querySelector('.vc-ugc__sound');
      button.setAttribute('aria-pressed', String(turnOn));
      button.setAttribute('aria-label', turnOn ? 'Turn sound off' : 'Turn sound on');
    }
  }

  if (!customElements.get('vc-gallery')) customElements.define('vc-gallery', VcGallery);
  if (!customElements.get('vc-ugc')) customElements.define('vc-ugc', VcUgc);
  if (!customElements.get('vc-buy-box')) customElements.define('vc-buy-box', VcBuyBox);
  if (!customElements.get('vc-quick-add')) customElements.define('vc-quick-add', VcQuickAdd);
})();
